import React, { useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpOnSquareIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useInstallPrompt } from '../../hooks/useInstallPrompt';
import { useToast } from '../../contexts/ToastContext';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import { displayName } from '../../utils/brand';

/**
 * Az app kezdőképernyőre telepítése. Chrome/Edge alatt egy gombbal megnyitja a
 * böngésző telepítési párbeszédét; iPhone-on csak leírni tudjuk a lépéseket,
 * mert a Safari nem ad rá programozott felületet.
 */
const InstallAppCard: React.FC = () => {
  const { mode, install, busy } = useInstallPrompt();
  const { settings } = useClubSettings();
  const { showToast } = useToast();
  const [dismissedResult, setDismissedResult] = useState(false);

  const appName = displayName(settings?.clubName);

  const handleInstall = async () => {
    const outcome = await install();

    if (outcome === 'accepted') {
      showToast('Az app telepítve.');
    } else if (outcome === 'dismissed') {
      setDismissedResult(true);
    } else {
      showToast('A telepítés most nem érhető el ebben a böngészőben.', 'info');
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden">
      <div className="px-4 md:px-6 py-4 border-b border-gray-100 card-header bg-gradient-to-r from-primary-50 to-white">
        <div className="flex items-center space-x-3">
          <div className="card-header-icon p-2 bg-primary-100 rounded-lg">
            <DevicePhoneMobileIcon className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Alkalmazás telepítése</h2>
            <p className="text-sm text-gray-500">
              A {appName} önálló appként is használható, böngészősáv nélkül.
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6">
        {mode === 'installed' && (
          <div className="flex items-start gap-3 rounded-lg bg-green-50 p-4">
            <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
            <div className="text-sm text-green-900">
              <p className="font-medium">Az app telepítve van ezen az eszközön.</p>
              <p className="mt-1 opacity-90">Innentől a kezdőképernyőről is elindíthatod.</p>
            </div>
          </div>
        )}

        {mode === 'prompt' && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-gray-600">
              Egy kattintással kiteheted a kezdőképernyőre. Telepítve gyorsabban indul, és
              az értesítések is megbízhatóbban érkeznek.
            </p>
            <button
              type="button"
              onClick={handleInstall}
              disabled={busy}
              className="flex w-full shrink-0 items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50 sm:w-auto"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {busy ? 'Telepítés...' : 'Telepítés'}
            </button>
          </div>
        )}

        {mode === 'ios-manual' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              iPhone-on és iPaden a Safari nem enged automatikus telepítést, de két
              koppintással kész:
            </p>
            <ol className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <ArrowUpOnSquareIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600" />
                <span>
                  Koppints a <strong>Megosztás</strong> gombra a böngésző alsó sávjában.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <PlusIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600" />
                <span>
                  Válaszd a <strong>Főképernyőhöz adás</strong> lehetőséget, majd erősítsd meg.
                </span>
              </li>
            </ol>
            <p className="text-xs text-gray-400">
              Az értesítések iPhone-on csak így, telepített appból működnek.
            </p>
          </div>
        )}

        {mode === 'unsupported' && (
          <p className="text-sm text-gray-500">
            Ez a böngésző nem támogatja az automatikus telepítést. Chrome vagy Edge alatt
            egy gombbal kitehető a kezdőképernyőre — más böngészőkben a menüből keresd
            a „Telepítés" vagy „Hozzáadás a kezdőképernyőhöz" lehetőséget.
          </p>
        )}

        {dismissedResult && mode !== 'installed' && (
          <p className="mt-3 text-sm text-gray-500">
            A telepítés megszakadt. Bármikor újrapróbálhatod — ha a gomb eltűnt, frissítsd az oldalt.
          </p>
        )}
      </div>
    </div>
  );
};

export default InstallAppCard;
